import { createSlice } from "@reduxjs/toolkit";

/**
 * Sidebar "My Tasks" list — sourced ENTIRELY from the
 * `/task/events/recent` Server-Sent Events stream (see
 * `useRecentTasksStream`). The old `/task/` GET + interval polling is
 * gone; each stream frame is a full snapshot that replaces the list here.
 *
 * `loaded` gates the Sidebar's skeleton: false until the first frame
 * arrives, so the list shows placeholders rather than an empty state
 * during the initial connect.
 */
const initialState = {
  tasks: [],
  loaded: false,
};

const recentTasksSlice = createSlice({
  name: "recentTasks",
  initialState,
  reducers: {
    // A full snapshot from the stream replaces the list wholesale.
    recentTasksSet(state, action) {
      state.tasks = Array.isArray(action.payload) ? action.payload : [];
      state.loaded = true;
    },
    // Optimistic local removal on delete, so the row disappears
    // immediately instead of waiting for the stream to re-push. The next
    // snapshot reconciles.
    recentTaskRemoved(state, action) {
      const id = String(action.payload ?? "");
      if (!id) return;
      state.tasks = state.tasks.filter(
        (t) => String(t?.task_id ?? t?.id ?? "") !== id,
      );
    },
    // Clear on logout so the next account never sees the previous one's
    // tasks.
    recentTasksReset() {
      return initialState;
    },
  },
});

export const { recentTasksSet, recentTaskRemoved, recentTasksReset } =
  recentTasksSlice.actions;

// Defensive reads: the slice is registered in the app store, but a
// consumer mounted in a lighter test/store without this reducer would
// otherwise crash on ``state.recentTasks`` being undefined.
export const selectRecentTasks = (state) => state.recentTasks?.tasks ?? [];
export const selectRecentTasksLoaded = (state) =>
  state.recentTasks?.loaded ?? false;

export default recentTasksSlice.reducer;
